// (c) chaehongee. Confidential and proprietary.

import { useCallback, useRef } from 'react'
import { useAppDispatch } from '../../../store/hooks/hooks'
import { mergeSessionEntities } from '../../../store/slices/sessionSlice'
import { getSessionInfo } from '../../../services/session'

/**
 * Hook to refresh the current session entities (nodes, commits) from the backend.
 * Useful after operations that modify the session state (e.g. generating images, uploading files).
 */
export function useSessionRefresh(sessionId: string | null) {
    const dispatch = useAppDispatch()
    const inFlightRef = useRef<Promise<void> | null>(null)
    const enqueueNextRef = useRef(false)

    const refreshSession = useCallback(async () => {
        if (!sessionId) return
        if (inFlightRef.current) {
            enqueueNextRef.current = true
            return inFlightRef.current
        }

        const run = async () => {
            do {
                enqueueNextRef.current = false
                try {
                    const info = await getSessionInfo(sessionId)
                    dispatch(mergeSessionEntities({
                        sessionId,
                        nodes: info.nodes,
                        commits: info.commits,
                    }))
                } catch (error) {
                    console.error('Failed to refresh session:', error)
                    break
                }
            } while (enqueueNextRef.current)
        }

        const promise = run().finally(() => {
            if (inFlightRef.current === promise) {
                inFlightRef.current = null
            }
        })
        inFlightRef.current = promise
        return promise
    }, [sessionId, dispatch])

    return refreshSession
}
